import { API, API_URL } from '@/config';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Globe, User, Mail, Crown, RefreshCw, AlertTriangle, Loader2, Clock, Calendar, Timer, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { switchLanguagePath } from '@/utils/localizedRoutes';
import T from '@/components/StyledTranslation';
import { BackButton } from '@/components/BackButton';



const SettingsPage = () => {
  const { t, i18n } = useTranslation();
  const { user, token, updateLanguage, logout } = useAuth();
  const navigate = useNavigate();
  
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [refundReason, setRefundReason] = useState('');
  const [submittingRefund, setSubmittingRefund] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [now, setNow] = useState(new Date());
  
  // Delete account states
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  
  // Update time every minute for countdown
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);
  
  // Calculate remaining time for demo accounts
  const getRemainingTime = (expiresAt) => {
    if (!expiresAt) return null;
    
    const expiry = new Date(expiresAt);
    const diff = expiry - now;
    
    if (diff <= 0) {
      return { expired: true, text: t('dashboard.expired') };
    }
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (days > 0) {
      return { 
        expired: false, 
        text: `${days}d ${hours}h`,
        urgent: false
      };
    } else if (hours > 0) {
      return { 
        expired: false, 
        text: `${hours}h ${minutes}m`,
        urgent: hours < 2
      };
    } else {
      return { 
        expired: false, 
        text: `${minutes}m`,
        urgent: true
      };
    }
  };
  
  // التحقق إذا كان المستخدم مشترك
  const isSubscribed = user?.subscription_status === 'active' || user?.is_admin || user?.role === 'super_admin';
  
  // التحقق إذا كان المستخدم لديه اشتراك مدفوع (ليس مجاني أو تجريبي أو مشرف)
  const freePlanNames = ['free', 'trial', 'gratis', 'free_trial', 'lifetime', 'demo'];
  const isAdmin = user?.is_admin || user?.role === 'super_admin' || user?.role === 'admin';
  const isDemoAccount = user?.is_demo_account === true;
  
  // تحسين الشرط - إظهار زر الاسترداد لجميع المشتركين المدفوعين (ليس للحسابات التجريبية)
  const subscriptionPlan = (user?.subscription_plan || '').toLowerCase();
  // BUG FIX: only show refund button while the 14-day EU refund window is still open.
  // The server also enforces this — the frontend hides the button proactively so
  // users don't see a UI they cannot actually use.
  const refundEligible = subscriptionStatus?.refund_eligible === true;
  const refundDaysRemaining = subscriptionStatus?.refund_days_remaining || 0;

  const hasPaidSubscription = 
    !isAdmin && // ليس مشرف
    !isDemoAccount && // ليس حساب تجريبي
    user?.subscription_status === 'active' && 
    subscriptionPlan && // لديه اسم خطة
    !freePlanNames.includes(subscriptionPlan) &&
    !user?.refund_requested &&
    refundEligible; // ضمن نافذة الاسترداد 14 يوماً
  
  // Demo account timer
  const demoRemaining = isDemoAccount ? getRemainingTime(user?.expires_at) : null;
  
  // Debug log
  console.log('Refund button check:', {
    isAdmin,
    status: user?.subscription_status,
    plan: subscriptionPlan,
    planId: user?.subscription_plan_id,
    refundRequested: user?.refund_requested,
    hasPaidSubscription
  });

  // Fetch subscription status
  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      if (!token || !user) return;
      
      setLoadingStatus(true);
      try {
        const response = await axios.get(`${API}/subscription/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSubscriptionStatus(response.data);
      } catch (error) {
        console.error('Error fetching subscription status:', error);
      } finally {
        setLoadingStatus(false);
      }
    };

    fetchSubscriptionStatus();
  }, [token, user]);

  const handleLanguageChange = (newLang) => {
    localStorage.setItem('i18nextLng', newLang);
    const newPath = switchLanguagePath(window.location.pathname, newLang);
    window.location.href = newPath;
  };

  const handleRefundRequest = async () => {
    if (!refundReason.trim()) {
      toast.error(t('ui.refundReason'));
      return;
    }

    setSubmittingRefund(true);
    try {
      await axios.post(
        `${API}/refund/request`,
        { reason: refundReason.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(t('ui.refundRequestSuccess'));
      setRefundModalOpen(false);
      setRefundReason('');
      // Refresh page to update user status
      window.location.reload();
    } catch (error) {
      console.error('Error requesting refund:', error);
      // Map backend error codes to translation keys
      const errorDetail = error.response?.data?.detail;
      let errorMessage = t('common.error');
      
      if (errorDetail === 'refund_already_requested') {
        errorMessage = t('ui.refundAlreadyRequested');
      } else if (errorDetail === 'no_active_subscription') {
        errorMessage = t('ui.noActiveSubscription');
      } else if (errorDetail === 'refund_window_expired') {
        // 14-day window closed — mirrors backend guard.
        errorMessage = i18n.language === 'ar'
          ? 'انتهت نافذة الاسترداد (14 يوماً من تاريخ الشراء).'
          : i18n.language === 'sv'
            ? 'Återbetalningsfönstret (14 dagar från köpet) har gått ut.'
            : 'The 14-day refund window has expired.';
      } else if (errorDetail === 'user_not_found') {
        errorMessage = t('common.error');
      }
      // Don't show raw English error messages, use generic error
      
      toast.error(errorMessage);
    } finally {
      setSubmittingRefund(false);
    }
  };

  // Handle delete account
  const handleDeleteAccount = async () => {
    setDeletingAccount(true);
    try {
      await axios.post(
        `${API}/auth/delete-account`,
        { 
          password: deletePassword,
          reason: deleteReason.trim() || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(
        i18n.language === 'ar' 
          ? 'تم حذف حسابك بنجاح. يمكنك إنشاء حساب جديد في أي وقت.'
          : i18n.language === 'sv'
          ? 'Ditt konto har raderats. Du kan skapa ett nytt konto när som helst.'
          : 'Your account has been deleted. You can create a new account anytime.'
      );
      
      setDeleteModalOpen(false);
      logout();
      navigate('/');
    } catch (error) {
      console.error('Error deleting account:', error);
      const errorMsg = error.response?.data?.detail;
      if (errorMsg === 'Incorrect password') {
        toast.error(
          i18n.language === 'ar' ? 'كلمة المرور غير صحيحة' 
          : i18n.language === 'sv' ? 'Felaktigt lösenord'
          : 'Incorrect password'
        );
      } else {
        toast.error(errorMsg || t('common.error'));
      }
    } finally {
      setDeletingAccount(false);
    }
  };

  // Format remaining time text
  const getRemainingTimeText = () => {
    if (!subscriptionStatus || !subscriptionStatus.has_subscription) return null;
    
    if (subscriptionStatus.status_detail === 'lifetime') {
      return t('subscription.neverExpires');
    }
    
    if (subscriptionStatus.is_expired) {
      return t('dashboard.expired');
    }
    
    const days = subscriptionStatus.days_remaining;
    const hours = subscriptionStatus.hours_remaining;
    
    if (days === 0) {
      return `${hours} ${t('dashboard.daysLeft').replace('days', 'hours')}`;
    }
    
    if (days === 1) {
      return `1 ${t('dashboard.daysLeft').replace('days', 'day')}`;
    }
    
    return `${days} ${t('dashboard.daysLeft')}`;
  };

  // Get badge color based on status
  const getStatusBadgeColor = () => {
    if (!subscriptionStatus) return 'bg-slate-100 text-slate-700';
    
    if (subscriptionStatus.status_detail === 'lifetime') {
      return 'bg-amber-100 text-amber-700';
    }
    
    if (subscriptionStatus.is_expired || subscriptionStatus.status === 'expired') {
      return 'bg-red-100 text-red-700';
    }
    
    if (subscriptionStatus.status_detail === 'expiring_soon') {
      return 'bg-orange-100 text-orange-700';
    }
    
    if (subscriptionStatus.status === 'active') {
      return 'bg-emerald-100 text-emerald-700';
    }
    
    if (subscriptionStatus.status === 'trial') {
      return 'bg-amber-100 text-amber-700';
    }
    
    return 'bg-slate-100 text-slate-700';
  };

  // صفحة الإعدادات متاحة للجميع - لا حاجة للقفل
  return (
    <div className="min-h-screen flex flex-col bg-slate-50" data-testid="settings-page">
      <Navbar />
      
      <main className="flex-grow pt-24 pb-12">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <BackButton to="/oversikt" className="mb-4" />
          <h1 className="text-3xl font-bold text-slate-900 mb-8"><T k="settings.title" /></h1>

          {/* Profile Info */}
          <div className="bg-white rounded-2xl p-6 border border-slate-200 mb-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <User className="w-5 h-5" />
              {t('ui.profile')}
            </h2>
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl">
                <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">{user?.name}</p>
                  <p className="text-sm text-slate-500"><T k="auth.name" /></p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl">
                <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                  <Mail className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">{user?.email}</p>
                  <p className="text-sm text-slate-500"><T k="auth.email" /></p>
                </div>
              </div>
            </div>
          </div>

          {/* Subscription */}
          <div className="bg-white rounded-2xl p-6 border border-slate-200 mb-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Crown className="w-5 h-5" />
              <span className="text-xl">🚗</span>
              B-körkort <T k="dashboard.subscription" />
            </h2>
            
            {/* Demo Account Timer Banner */}
            {isDemoAccount && demoRemaining && (
              <div className={`mb-4 p-4 rounded-xl border ${
                demoRemaining.expired 
                  ? 'bg-red-50 border-red-200' 
                  : demoRemaining.urgent 
                    ? 'bg-amber-50 border-amber-200' 
                    : 'bg-cyan-50 border-cyan-200'
              }`}>
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      demoRemaining.expired 
                        ? 'bg-red-100' 
                        : demoRemaining.urgent 
                          ? 'bg-amber-100' 
                          : 'bg-cyan-100'
                    }`}>
                      <Timer className={`w-6 h-6 ${
                        demoRemaining.expired 
                          ? 'text-red-600' 
                          : demoRemaining.urgent 
                            ? 'text-amber-600' 
                            : 'text-cyan-600'
                      }`} />
                    </div>
                    <div>
                      <p className={`font-bold ${
                        demoRemaining.expired 
                          ? 'text-red-700' 
                          : demoRemaining.urgent 
                            ? 'text-amber-700' 
                            : 'text-cyan-700'
                      }`}>
                        {t('ui.demoAccount')}
                      </p>
                      <p className={`text-sm ${
                        demoRemaining.expired 
                          ? 'text-red-600' 
                          : demoRemaining.urgent 
                            ? 'text-amber-600' 
                            : 'text-cyan-600'
                      }`}>
                        {demoRemaining.expired 
                          ? t('ui.accountExpired')
                          : t('ui.timeRemaining')
                        }
                      </p>
                    </div>
                  </div>
                  <div className={`text-2xl font-bold px-4 py-2 rounded-xl ${
                    demoRemaining.expired 
                      ? 'bg-red-100 text-red-700' 
                      : demoRemaining.urgent 
                        ? 'bg-amber-100 text-amber-700' 
                        : 'bg-cyan-100 text-cyan-700'
                  }`}>
                    {demoRemaining.text}
                  </div>
                </div>
              </div>
            )}
            
            <div className="p-4 bg-slate-50 rounded-xl">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="font-medium text-slate-900">
                    {isDemoAccount 
                      ? <span className="flex items-center gap-2">🎭 {t('ui.demoFullAccess')}</span>
                      : user?.subscription_plan === 'lifetime' 
                        ? <span className="flex items-center gap-2">👑 {t('subscription.lifetime')}</span>
                        : user?.subscription_status === 'active' 
                          ? <>{t('dashboard.active')} - {user?.subscription_plan === 'six_months' ? t('pricing.sixMonths') : t('pricing.monthly')}</>
                          : user?.subscription_status === 'trial' 
                            ? t('dashboard.trial')
                            : t('dashboard.expired')
                    }
                  </p>
                  {user?.subscription_plan === 'lifetime' ? (
                    <p className="text-sm text-amber-600 font-medium">
                      {t('subscription.neverExpires')}
                    </p>
                  ) : isDemoAccount ? (
                    <p className="text-sm text-cyan-600">
                      {t('ui.enjoyFeatures')}
                    </p>
                  ) : (
                    <>
                      {user?.subscription_end_date && (
                        <p className="text-sm text-slate-500">
                          {t('settings.expires')}: {new Date(user.subscription_end_date).toLocaleDateString()}
                        </p>
                      )}
                      {user?.trial_end_date && user?.subscription_status === 'trial' && (
                        <p className="text-sm text-slate-500">
                          {t('settings.trialEnds')}: {new Date(user.trial_end_date).toLocaleDateString()}
                        </p>
                      )}
                    </>
                  )}
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeColor()}`}>
                  {user?.subscription_plan === 'lifetime'
                    ? <span>👑 {t('subscription.lifetime')}</span>
                    : user?.subscription_status === 'active' 
                      ? t('dashboard.active')
                      : user?.subscription_status === 'trial'
                        ? t('dashboard.trial')
                        : t('dashboard.expired')
                  }
                </span>
              </div>
            </div>
            
            {/* Remaining Time Card - Only for active paid subscriptions */}
            {subscriptionStatus && subscriptionStatus.has_subscription && 
              user?.subscription_status === 'active' && 
              subscriptionStatus.status_detail !== 'lifetime' && (
              <div className={`mt-4 p-4 rounded-xl border ${
                subscriptionStatus.status_detail === 'expiring_soon' 
                  ? 'bg-orange-50 border-orange-200' 
                  : subscriptionStatus.is_expired 
                    ? 'bg-red-50 border-red-200'
                    : 'bg-emerald-50 border-emerald-200'
              }`}>
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    subscriptionStatus.status_detail === 'expiring_soon' 
                      ? 'bg-orange-100' 
                      : subscriptionStatus.is_expired 
                        ? 'bg-red-100'
                        : 'bg-emerald-100'
                  }`}>
                    <Clock className={`w-6 h-6 ${
                      subscriptionStatus.status_detail === 'expiring_soon' 
                        ? 'text-orange-600' 
                        : subscriptionStatus.is_expired 
                          ? 'text-red-600'
                          : 'text-emerald-600'
                    }`} />
                  </div>
                  <div className="flex-1">
                    <p className={`font-bold text-lg ${
                      subscriptionStatus.status_detail === 'expiring_soon' 
                        ? 'text-orange-700' 
                        : subscriptionStatus.is_expired 
                          ? 'text-red-700'
                          : 'text-emerald-700'
                    }`}>
                      {getRemainingTimeText()}
                    </p>
                    {subscriptionStatus.end_date_formatted && (
                      <p className="text-sm text-slate-600 flex items-center gap-1 mt-1">
                        <Calendar className="w-4 h-4" />
                        {t('settings.expires')}: {subscriptionStatus.end_date_formatted}
                      </p>
                    )}
                  </div>
                </div>
                
                {/* Renew button for expiring soon */}
                {(subscriptionStatus.status_detail === 'expiring_soon' || subscriptionStatus.is_expired) && (
                  <button
                    onClick={() => navigate('/priser')}
                    className="mt-4 w-full py-3 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold rounded-xl shadow-lg transition-all flex items-center justify-center gap-2"
                    data-testid="renew-btn"
                  >
                    🔄 {t('ui.renewSubscription')}
                  </button>
                )}
              </div>
            )}

            {/* Expired Subscription Alert */}
            {(user?.subscription_status === 'expired' || subscriptionStatus?.is_expired) && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
                <div className="flex items-center gap-2 text-red-700 mb-2">
                  <AlertTriangle className="w-5 h-5" />
                  <span className="font-bold">
                    {t('dashboard.expired')}!
                  </span>
                </div>
                <p className="text-sm text-red-600 mb-4">
                  {t('ui.subscribeDesc')}
                </p>
                <button
                  onClick={() => navigate('/priser')}
                  className="w-full py-3 bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 text-white font-bold rounded-xl shadow-lg transition-all"
                  data-testid="renew-expired-btn"
                >
                  🚀 {t('ui.renewNow')}
                </button>
              </div>
            )}
            
            {/* زر الترقية للمستخدمين غير المشتركين */}
            {!isSubscribed && user?.subscription_status !== 'expired' && (
              <button
                onClick={() => navigate('/priser')}
                className="mt-4 w-full py-3 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold rounded-xl shadow-lg transition-all"
                data-testid="upgrade-btn"
              >
                🚀 {t('ui.upgradeNow')}
              </button>
            )}

            {/* زر طلب الاسترداد للمشتركين */}
            {hasPaidSubscription && (
              <button
                onClick={() => setRefundModalOpen(true)}
                className="mt-4 w-full py-3 bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200 font-medium rounded-xl transition-all flex items-center justify-center gap-2"
                data-testid="request-refund-btn"
              >
                <RefreshCw className="w-4 h-4" />
                {t('ui.requestRefund')}
              </button>
            )}

            {/* رسالة إذا كان طلب الاسترداد قيد المراجعة */}
            {user?.refund_requested && (
              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <div className="flex items-center gap-2 text-amber-700">
                  <AlertTriangle className="w-5 h-5" />
                  <span className="font-medium">
                    {t('ui.refundUnderReview')}
                  </span>
                </div>
                <p className="text-sm text-amber-600 mt-1">
                  {t('ui.refundContactSoon')}
                </p>
              </div>
            )}
          </div>

          {/* Taxikort Subscription — only rendered when the user has a Taxi
              subscription state (active / expired). Trial users don't need
              a second card. */}
          {(user?.taxi_subscription_status === 'active' || user?.taxi_subscription_status === 'expired') && (
            <div className="bg-white rounded-2xl p-6 border border-slate-200 mb-6" data-testid="taxi-subscription-card">
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <span className="text-2xl">🚕</span>
                Taxikort {t('dashboard.subscription')}
              </h2>

              <div className="p-4 bg-amber-50 rounded-xl border border-amber-100">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <p className="font-medium text-slate-900">
                      {user?.taxi_subscription_plan === 'lifetime'
                        ? <span className="flex items-center gap-2">👑 {t('subscription.lifetime')}</span>
                        : user?.taxi_subscription_status === 'active'
                          ? <span className="flex items-center gap-2">✅ Taxikort {t('dashboard.active')}</span>
                          : <span className="flex items-center gap-2">⚠️ Taxikort {t('dashboard.expired')}</span>
                      }
                    </p>
                    {user?.taxi_subscription_plan === 'lifetime' ? (
                      <p className="text-sm text-amber-600 font-medium">
                        {t('subscription.neverExpires')}
                      </p>
                    ) : user?.taxi_subscription_end_date && (
                      <p className="text-sm text-slate-500">
                        {t('settings.expires')}: {new Date(user.taxi_subscription_end_date).toLocaleDateString()}
                      </p>
                    )}
                    {user?.taxi_subscription_plan && user.taxi_subscription_plan !== 'lifetime' && (
                      <p className="text-xs text-slate-400 mt-1">
                        {t('pricing.plan') || 'Plan'}: {user.taxi_subscription_plan}
                      </p>
                    )}
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    user?.taxi_subscription_plan === 'lifetime'
                      ? 'bg-amber-100 text-amber-700'
                      : user?.taxi_subscription_status === 'active'
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-red-100 text-red-700'
                  }`}>
                    {user?.taxi_subscription_plan === 'lifetime'
                      ? <span>👑 {t('subscription.lifetime')}</span>
                      : user?.taxi_subscription_status === 'active'
                        ? t('dashboard.active')
                        : t('dashboard.expired')
                    }
                  </span>
                </div>
              </div>

              {/* Renew CTA for expired Taxi */}
              {user?.taxi_subscription_status === 'expired' && (
                <button
                  onClick={() => navigate('/priser?licenseType=taxi')}
                  className="mt-4 w-full py-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold rounded-xl shadow-lg transition-all"
                  data-testid="renew-taxi-btn"
                >
                  🔄 {t('ui.renewNow')}
                </button>
              )}
            </div>
          )}

          {/* Language Settings */}
          <div className="bg-white rounded-2xl p-6 border border-slate-200">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5" />
              <T k="settings.language" />
            </h2>
            <div className="space-y-2">
              <Label htmlFor="language" className="text-slate-600">
                <T k="settings.language" />
              </Label>
              <Select value={i18n.language} onValueChange={handleLanguageChange}>
                <SelectTrigger className="h-12 rounded-xl bg-slate-50 border-slate-200" data-testid="language-select">
                  <SelectValue>
                    <span className="flex items-center gap-2">
                      {i18n.language === 'sv' && <><img src="https://flagcdn.com/20x15/se.png" alt="SE" width="20" height="15" className="rounded-sm" /> Svenska</>}
                      {i18n.language === 'ar' && <><img src="https://flagcdn.com/20x15/sa.png" alt="SA" width="20" height="15" className="rounded-sm" /> العربية</>}
                      {i18n.language === 'en' && <><img src="https://flagcdn.com/20x15/gb.png" alt="GB" width="20" height="15" className="rounded-sm" /> English</>}
                      {i18n.language === 'tr' && <><img src="https://flagcdn.com/20x15/tr.png" alt="TR" width="20" height="15" className="rounded-sm" /> Türkçe</>}
                      {i18n.language === 'fa' && <><img src="https://flagcdn.com/20x15/ir.png" alt="IR" width="20" height="15" className="rounded-sm" /> فارسی</>}
                      {i18n.language === 'so' && <><img src="https://flagcdn.com/20x15/so.png" alt="SO" width="20" height="15" className="rounded-sm" /> Soomaali</>}
                      {i18n.language === 'pl' && <><img src="https://flagcdn.com/20x15/pl.png" alt="PL" width="20" height="15" className="rounded-sm" /> Polski</>}
                      {i18n.language === 'es' && <><img src="https://flagcdn.com/20x15/es.png" alt="ES" width="20" height="15" className="rounded-sm" /> Español</>}
                      {i18n.language === 'de' && <><img src="https://flagcdn.com/20x15/de.png" alt="DE" width="20" height="15" className="rounded-sm" /> Deutsch</>}
                    </span>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="sv"><span className="flex items-center gap-2"><img src="https://flagcdn.com/20x15/se.png" alt="SE" width="20" height="15" className="rounded-sm" /> Svenska</span></SelectItem>
                  <SelectItem value="ar"><span className="flex items-center gap-2"><img src="https://flagcdn.com/20x15/sa.png" alt="SA" width="20" height="15" className="rounded-sm" /> العربية</span></SelectItem>
                  <SelectItem value="en"><span className="flex items-center gap-2"><img src="https://flagcdn.com/20x15/gb.png" alt="GB" width="20" height="15" className="rounded-sm" /> English</span></SelectItem>
                  <SelectItem value="tr"><span className="flex items-center gap-2"><img src="https://flagcdn.com/20x15/tr.png" alt="TR" width="20" height="15" className="rounded-sm" /> Türkçe</span></SelectItem>
                  <SelectItem value="fa"><span className="flex items-center gap-2"><img src="https://flagcdn.com/20x15/ir.png" alt="IR" width="20" height="15" className="rounded-sm" /> فارسی</span></SelectItem>
                  <SelectItem value="so"><span className="flex items-center gap-2"><img src="https://flagcdn.com/20x15/so.png" alt="SO" width="20" height="15" className="rounded-sm" /> Soomaali</span></SelectItem>
                  <SelectItem value="pl"><span className="flex items-center gap-2"><img src="https://flagcdn.com/20x15/pl.png" alt="PL" width="20" height="15" className="rounded-sm" /> Polski</span></SelectItem>
                  <SelectItem value="es"><span className="flex items-center gap-2"><img src="https://flagcdn.com/20x15/es.png" alt="ES" width="20" height="15" className="rounded-sm" /> Español</span></SelectItem>
                  <SelectItem value="de"><span className="flex items-center gap-2"><img src="https://flagcdn.com/20x15/de.png" alt="DE" width="20" height="15" className="rounded-sm" /> Deutsch</span></SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Delete Account Section */}
          {!isAdmin && (
            <div className="bg-white rounded-2xl p-6 border border-red-200">
              <h2 className="text-lg font-semibold text-red-600 mb-4 flex items-center gap-2">
                <Trash2 className="w-5 h-5" />
                {{
                  ar: 'حذف الحساب',
                  sv: 'Radera konto',
                  en: 'Delete Account',
                  tr: 'Hesabı Sil',
                  fa: 'حذف حساب',
                  so: 'Tirtir Akoonka',
                  pl: 'Usuń konto',
                  es: 'Eliminar cuenta',
                  de: 'Konto löschen'
                }[i18n.language] || 'Delete Account'}
              </h2>
              <p className="text-sm text-slate-600 mb-4">
                {{
                  ar: 'بمجرد حذف حسابك، لن تتمكن من تسجيل الدخول مرة أخرى. يمكنك إنشاء حساب جديد في أي وقت.',
                  sv: 'När du raderar ditt konto kan du inte logga in igen. Du kan skapa ett nytt konto när som helst.',
                  en: 'Once you delete your account, you will not be able to login again. You can create a new account anytime.',
                  tr: 'Hesabınızı sildiğinizde tekrar giriş yapamazsınız. İstediğiniz zaman yeni bir hesap oluşturabilirsiniz.',
                  fa: 'پس از حذف حساب، دیگر نمی‌توانید وارد شوید. هر زمان می‌توانید حساب جدید بسازید.',
                  so: 'Marka aad tirtirto akoonkaaga, ma awoodid inaad mar kale gasho. Waxaad abuuri kartaa akoon cusub markasta.',
                  pl: 'Po usunięciu konta nie będziesz mógł się ponownie zalogować. W każdej chwili możesz utworzyć nowe konto.',
                  es: 'Una vez que elimines tu cuenta, no podrás iniciar sesión de nuevo. Puedes crear una nueva cuenta en cualquier momento.',
                  de: 'Sobald Sie Ihr Konto löschen, können Sie sich nicht mehr anmelden. Sie können jederzeit ein neues Konto erstellen.'
                }[i18n.language] || 'Once you delete your account, you will not be able to login again. You can create a new account anytime.'}
              </p>
              <Button
                onClick={() => setDeleteModalOpen(true)}
                variant="outline"
                className="w-full border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                data-testid="delete-account-btn"
              >
                <Trash2 className="w-4 h-4 me-2" />
                {{
                  ar: 'حذف حسابي',
                  sv: 'Radera mitt konto',
                  en: 'Delete My Account',
                  tr: 'Hesabımı Sil',
                  fa: 'حذف حساب من',
                  so: 'Tirtir Akoonkayga',
                  pl: 'Usuń moje konto',
                  es: 'Eliminar mi cuenta',
                  de: 'Mein Konto löschen'
                }[i18n.language] || 'Delete My Account'}
              </Button>
            </div>
          )}
        </div>
      </main>

      {/* Refund Request Modal */}
      <Dialog open={refundModalOpen} onOpenChange={setRefundModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <RefreshCw className="w-5 h-5" />
              {t('ui.requestRefund')}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-700">
                {t('ui.refundContactSoon')}
              </p>
            </div>

            <div>
              <Label className="text-slate-700 mb-2 block">
                {t('ui.refundReason')} *
              </Label>
              <Textarea
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                placeholder={t('ui.explainRefund')}
                rows={4}
                className="resize-none"
                data-testid="refund-reason-input"
              />
            </div>
          </div>

          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => setRefundModalOpen(false)} disabled={submittingRefund}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleRefundRequest}
              disabled={submittingRefund || !refundReason.trim()}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="submit-refund-btn"
            >
              {submittingRefund ? (
                <>
                  <Loader2 className="w-4 h-4 me-2 animate-spin" />
                  {t('ui.submitting')}
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 me-2" />
                  {t('ui.submitRequest')}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Account Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="sm:max-w-md" data-testid="delete-account-modal">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              {{
                ar: 'حذف الحساب',
                sv: 'Radera konto',
                en: 'Delete Account',
                tr: 'Hesabı Sil',
                fa: 'حذف حساب',
                so: 'Tirtir Akoonka',
                pl: 'Usuń konto',
                es: 'Eliminar cuenta',
                de: 'Konto löschen'
              }[i18n.language] || 'Delete Account'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-700">
                {{
                  ar: '⚠️ هذا الإجراء لا يمكن التراجع عنه. سيتم حذف حسابك نهائياً ولن تتمكن من تسجيل الدخول مرة أخرى بنفس البريد.',
                  sv: '⚠️ Denna åtgärd kan inte ångras. Ditt konto kommer att raderas permanent och du kan inte logga in igen med samma e-post.',
                  en: '⚠️ This action cannot be undone. Your account will be permanently deleted and you cannot login again with the same email.',
                  tr: '⚠️ Bu işlem geri alınamaz. Hesabınız kalıcı olarak silinecek ve aynı e-posta ile tekrar giriş yapamazsınız.',
                  fa: '⚠️ این عمل قابل بازگشت نیست. حساب شما برای همیشه حذف خواهد شد و نمی‌توانید با همان ایمیل دوباره وارد شوید.',
                  so: '⚠️ Ficilkan lama noqon karo. Akoonkaaga waxaa laga tirtiri doonaa si joogto ah mana awoodid inaad mar kale gasho iimaylka la mid ah.',
                  pl: '⚠️ Tej akcji nie można cofnąć. Twoje konto zostanie trwale usunięte i nie będziesz mógł się ponownie zalogować tym samym e-mailem.',
                  es: '⚠️ Esta acción no se puede deshacer. Tu cuenta será eliminada permanentemente y no podrás iniciar sesión de nuevo con el mismo correo.',
                  de: '⚠️ Diese Aktion kann nicht rückgängig gemacht werden. Ihr Konto wird dauerhaft gelöscht und Sie können sich nicht mehr mit derselben E-Mail anmelden.'
                }[i18n.language] || '⚠️ This action cannot be undone. Your account will be permanently deleted and you cannot login again with the same email.'}
              </p>
            </div>

            {/* Only show password field for non-Google users */}
            {!user?.google_id && (
              <div>
                <Label className="text-slate-700 mb-2 block">
                  {{
                    ar: 'كلمة المرور *',
                    sv: 'Lösenord *',
                    en: 'Password *',
                    tr: 'Şifre *',
                    fa: 'رمز عبور *',
                    so: 'Furaha sirta ah *',
                    pl: 'Hasło *',
                    es: 'Contraseña *',
                    de: 'Passwort *'
                  }[i18n.language] || 'Password *'}
                </Label>
                <input
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder={{
                    ar: 'أدخل كلمة المرور للتأكيد',
                    sv: 'Ange lösenord för bekräftelse',
                    en: 'Enter password to confirm',
                    tr: 'Onaylamak için şifrenizi girin',
                    fa: 'رمز عبور را برای تأیید وارد کنید',
                    so: 'Geli furaha sirta ah si aad u xaqiijiso',
                    pl: 'Wprowadź hasło, aby potwierdzić',
                    es: 'Ingresa tu contraseña para confirmar',
                    de: 'Geben Sie Ihr Passwort zur Bestätigung ein'
                  }[i18n.language] || 'Enter password to confirm'}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  data-testid="delete-password-input"
                />
              </div>
            )}

            {/* Show info for Google users */}
            {user?.google_id && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-700">
                  {{
                    ar: '🔐 تم التسجيل عبر Google - لا حاجة لكلمة مرور',
                    sv: '🔐 Inloggad via Google - inget lösenord krävs',
                    en: '🔐 Logged in via Google - no password required',
                    tr: '🔐 Google ile giriş yapıldı - şifre gerekmez',
                    fa: '🔐 با Google وارد شده‌اید - نیازی به رمز عبور نیست',
                    so: '🔐 Waxaad ku gashay Google - furaha sirta ah looma baahna',
                    pl: '🔐 Zalogowano przez Google - hasło nie jest wymagane',
                    es: '🔐 Conectado con Google - no se requiere contraseña',
                    de: '🔐 Mit Google angemeldet - kein Passwort erforderlich'
                  }[i18n.language] || '🔐 Logged in via Google - no password required'}
                </p>
              </div>
            )}

            <div>
              <Label className="text-slate-700 mb-2 block">
                {{
                  ar: 'سبب الحذف (اختياري)',
                  sv: 'Anledning till radering (valfritt)',
                  en: 'Reason for deletion (optional)',
                  tr: 'Silme nedeni (isteğe bağlı)',
                  fa: 'دلیل حذف (اختیاری)',
                  so: 'Sababta tirtirka (ikhtiyaari)',
                  pl: 'Powód usunięcia (opcjonalnie)',
                  es: 'Motivo de eliminación (opcional)',
                  de: 'Grund für die Löschung (optional)'
                }[i18n.language] || 'Reason for deletion (optional)'}
              </Label>
              <Textarea
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                placeholder={{
                  ar: 'لماذا تريد حذف حسابك؟',
                  sv: 'Varför vill du radera ditt konto?',
                  en: 'Why do you want to delete your account?',
                  tr: 'Hesabınızı neden silmek istiyorsunuz?',
                  fa: 'چرا می‌خواهید حساب خود را حذف کنید؟',
                  so: 'Maxaad u rabta inaad tirtirto akoonkaaga?',
                  pl: 'Dlaczego chcesz usunąć swoje konto?',
                  es: '¿Por qué quieres eliminar tu cuenta?',
                  de: 'Warum möchten Sie Ihr Konto löschen?'
                }[i18n.language] || 'Why do you want to delete your account?'}
                rows={3}
                className="resize-none"
                data-testid="delete-reason-input"
              />
            </div>
          </div>

          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)} disabled={deletingAccount}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleDeleteAccount}
              disabled={deletingAccount || (!user?.google_id && !deletePassword)}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="confirm-delete-btn"
            >
              {deletingAccount ? (
                <>
                  <Loader2 className="w-4 h-4 me-2 animate-spin" />
                  {{
                    ar: 'جاري الحذف...',
                    sv: 'Raderar...',
                    en: 'Deleting...',
                    tr: 'Siliniyor...',
                    fa: 'در حال حذف...',
                    so: 'Waa la tirtirayaa...',
                    pl: 'Usuwanie...',
                    es: 'Eliminando...',
                    de: 'Wird gelöscht...'
                  }[i18n.language] || 'Deleting...'}
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 me-2" />
                  {{
                    ar: 'حذف الحساب نهائياً',
                    sv: 'Radera konto permanent',
                    en: 'Delete Account Permanently',
                    tr: 'Hesabı Kalıcı Olarak Sil',
                    fa: 'حذف دائمی حساب',
                    so: 'Si Joogto ah u Tirtir Akoonka',
                    pl: 'Usuń konto na stałe',
                    es: 'Eliminar cuenta permanentemente',
                    de: 'Konto dauerhaft löschen'
                  }[i18n.language] || 'Delete Account Permanently'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default SettingsPage;
